import { Component, Inject, Input, OnChanges, OnDestroy, Optional, SimpleChanges } from '@angular/core';
import {
  DwComponent,
  DwComponentType,
  DwEventService,
  DwExpressionService,
  DwMetaDataFormApi,
  DwMetaDataFormStateService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwModalService,
  DwSecurityUserService,
  DwSectionBaseComponent,
  DwUiConfigRegistryService,
  DwUIMetaDataConfig,
  DwUIMetaDataConfigToken
} from '@devwareapps/devware-cap';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AppPermissions } from '../../../../meta-data/app-permissions.enum';
import {
  AIProcessingReferenceStatusAllItems,
  AppMetaDataItemNames,
  CourseDocumentProcessingStatusAllItems,
  DocumentAiProcessingStatusAllItems,
  DocumentItemChunkContentTypeAllItems,
  DocumentItemEntity
} from '../../../../meta-data/app-meta-data.service';
import {
  DocumentAiProcessingTabKey,
  DocumentItemAiProcessingReferenceOption,
  DocumentItemAiProcessingCourseDetails,
  DocumentItemAiProcessingCourseModule,
  DocumentItemAiProcessingCourseSummary,
  DocumentItemAiProcessingLessonLink,
  DocumentItemAiProcessingSummary
} from '../../models/document-item-ai-processing.model';
import { DocumentsRepositoryService } from '../../services/documents-repository.service';
import { DwPubSubEventTopics } from '../../../z-devware/pubsub/models/dw-pub-sub-event-topics.model';
import { DwPubSubService } from '../../../z-devware/pubsub/dw-pub-sub-service';

@DwComponent({
  key: 'document-item-ai-processing',
  name: 'Document Item AI Processing',
  componentType: DwComponentType.formSection,
  isGlobal: false,
  parentItemName: AppMetaDataItemNames.DocumentItem
})
@Component({
  selector: 'app-document-item-ai-processing',
  templateUrl: './document-item-ai-processing.component.html',
  styleUrls: ['./document-item-ai-processing.component.scss']
})
export class DocumentItemAiProcessingComponent extends DwSectionBaseComponent implements OnChanges, OnDestroy {
  private static readonly DOCUMENT_AI_PROCESSING_UPDATED_TOPIC = 'document-ai-processing-updated';
  private static readonly LOCAL_DATE_TIME_FORMAT = 'M/d/yyyy h:mm a';
  private static readonly AI_PROCESSING_REFERENCE_TAB_NAME = 'aiProcessingReferenceDetail';
  private static readonly STALE_MODULE_PROCESSING_MILLISECONDS = 30 * 60 * 1000;
  private processingDurationTimerId?: number;

  @Input() documentItemId?: number;

  aiProcessing?: DocumentItemAiProcessingSummary;
  activeTab: DocumentAiProcessingTabKey = 'page-extraction';
  loading = false;
  loadAttempted = false;
  currentUtcNow = new Date();
  processingDurationLabel = 'Time Processing';
  chunkFilter: 'instructional' | 'other' | 'all' = 'instructional';
  showStaleModuleProcessingWarning = false;

  expandedPageId?: number;
  expandedChunkId?: number;
  expandedCourseDocumentItemId?: number;
  expandedCourseModuleDocumentItemId?: number;
  openActionMenuKey?: string;
  openAiReferenceChooserModuleId?: number;
  viewerDocumentItem?: DocumentItemEntity | null;
  viewerInitialPageNumber?: number;
  viewerPageNumbers?: string;
  loadingViewerDocument = false;

  private formApi?: DwMetaDataFormApi;
  private courseDetailsById: { [courseDocumentItemId: number]: DocumentItemAiProcessingCourseDetails } = {};
  private moduleLessonLinksByCourseModuleId: { [courseModuleId: number]: DocumentItemAiProcessingLessonLink[] } = {};
  private moduleInactiveLessonLinksByCourseModuleId: { [courseModuleId: number]: DocumentItemAiProcessingLessonLink[] } = {};
  private showInactiveLessonLinksByCourseModuleId: { [courseModuleId: number]: boolean } = {};
  private lessonLinkActionLoadingById: { [courseLessonDocumentLinkId: number]: boolean } = {};
  private moduleAiReferenceOptionsByCourseModuleId: { [courseModuleId: number]: DocumentItemAiProcessingReferenceOption[] } = {};
  private loadingCourseDocumentItemIds: { [courseDocumentItemId: number]: boolean } = {};
  private loadingCourseModuleIds: { [courseModuleId: number]: boolean } = {};
  private chunkActionLoadingById: { [documentItemChunkId: number]: boolean } = {};
  private courseActionLoadingById: { [courseDocumentItemId: number]: boolean } = {};
  private moduleActionLoadingById: { [courseModuleDocumentItemId: number]: boolean } = {};
  private readonly destroy$ = new Subject<void>();
  private readonly silentRefreshRequests$ = new Subject<void>();
  private pubSubRefreshSetup = false;

  constructor(
    dwExpressionService: DwExpressionService,
    protected dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    @Optional() private dwFormStateService: DwMetaDataFormStateService,
    private documentsRepositoryService: DocumentsRepositoryService,
    private dwEventService: DwEventService,
    private dwSecurityUserService: DwSecurityUserService,
    private dwPubSubService: DwPubSubService,
    private dwModalService: DwModalService
  ) {
    super(dwExpressionService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = this.dwFormStateService?.state?.formApi;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.documentItemId) {
      this.loadAiProcessing();
    }
  }

  setupComponentComplete(): void {
    this.runAfterFormDataLoaded(() => {
      if (!this.documentItemId && this.formApi) {
        const documentItem = this.formApi.getFormData<DocumentItemEntity>();
        this.documentItemId = documentItem?.DocumentItemId;
      }

      this.setupPubSubRefresh();
      this.loadAiProcessing();
    });
  }

  ngOnDestroy(): void {
    this.stopProcessingDurationTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasData(): boolean {
    return !!this.aiProcessing;
  }

  get chunkPercent(): number {
    return this.getPercent(
      this.aiProcessing?.DocumentAiProcessingProcessedChunkCount,
      this.aiProcessing?.DocumentAiProcessingTotalChunkCount
    );
  }

  get coursePercent(): number {
    const currentCourse = this.getCurrentCourseProcessingItem();

    if (currentCourse) {
      return this.getPercent(
        currentCourse.CourseDocumentProcessingProcessedModuleCount,
        currentCourse.CourseDocumentProcessingTotalModuleCount
      );
    }

    return this.getPercent(this.completedCourseCount, this.totalCourseCount);
  }

  get totalCourseCount(): number {
    return this.aiProcessing?.Courses?.length || 0;
  }

  get completedCourseCount(): number {
    return (this.aiProcessing?.Courses || []).filter(course =>
      course.CourseDocumentProcessingStatusId === CourseDocumentProcessingStatusAllItems.Complete).length;
  }

  get failedCourseCount(): number {
    return (this.aiProcessing?.Courses || []).filter(course =>
      course.CourseDocumentProcessingStatusId === CourseDocumentProcessingStatusAllItems.Failed).length;
  }

  get documentTabStarted(): boolean {
    return this.hasDocumentProcessingStarted();
  }

  get pageExtractionStarted(): boolean {
    return this.hasPageExtractionStarted();
  }

  get chunkProcessingStarted(): boolean {
    return this.hasChunkProcessingStarted();
  }

  get courseTabStarted(): boolean {
    return this.hasCourseProcessingStarted();
  }

  get showChunkProcessingStep(): boolean {
    return !this.isExtractionFailed() && this.hasChunkProcessingStarted();
  }

  get filteredChunks() {
    const chunks = this.aiProcessing?.Chunks || [];

    if (this.chunkFilter === 'all') {
      return chunks;
    }

    if (this.chunkFilter === 'other') {
      return chunks.filter(chunk => !this.isInstructionalChunk(chunk.DocumentItemChunkContentTypeId));
    }

    return chunks.filter(chunk => this.isInstructionalChunk(chunk.DocumentItemChunkContentTypeId));
  }

  get instructionalChunkCount(): number {
    return (this.aiProcessing?.Chunks || []).filter(chunk => this.isInstructionalChunk(chunk.DocumentItemChunkContentTypeId)).length;
  }

  get otherChunkCount(): number {
    return (this.aiProcessing?.Chunks || []).filter(chunk => !this.isInstructionalChunk(chunk.DocumentItemChunkContentTypeId)).length;
  }

  get showCourseProcessingStep(): boolean {
    return !this.isExtractionFailed()
      && !this.isChunkFailed()
      && this.isChunkProcessingComplete()
      && this.hasCourseProcessingStarted();
  }

  setActiveTab(tab: DocumentAiProcessingTabKey): void {
    this.activeTab = tab;
  }

  setChunkFilter(filter: 'instructional' | 'other' | 'all'): void {
    this.chunkFilter = filter;
  }

  refresh(): void {
    this.loadAiProcessing();
  }

  reprocess(): void {
    if (!this.documentItemId || !this.canReprocess || this.loading) {
      return;
    }

    this.applyPendingReprocessState();

    this.documentsRepositoryService.requestDocumentItemAiReprocess(this.documentItemId)
      .subscribe(() => {
        this.loadAiProcessing(false);
      }, () => {
        this.loadAiProcessing(false);
      });
  }

  get canReprocess(): boolean {
    const statusId = this.aiProcessing?.DocumentAiProcessingStatusId;

    return !statusId
      || statusId === DocumentAiProcessingStatusAllItems.Complete
      || statusId === DocumentAiProcessingStatusAllItems.Hold
      || statusId === DocumentAiProcessingStatusAllItems.FailedExtractingPagesAndChunks
      || statusId === DocumentAiProcessingStatusAllItems.FailedProcessingDocumentChunks
      || statusId === DocumentAiProcessingStatusAllItems.FailedProcessingModuleDocumentLinks;
  }

  get canReprocessChunks(): boolean {
    return this.canReprocess && !!this.aiProcessing?.Chunks?.length;
  }

  get showHoldOrPendingState(): boolean {
    const statusId = this.aiProcessing?.DocumentAiProcessingStatusId;

    return !statusId || statusId === DocumentAiProcessingStatusAllItems.Hold;
  }

  get showReprocessPendingState(): boolean {
    return this.aiProcessing?.DocumentAiProcessingStatusId === DocumentAiProcessingStatusAllItems.ReprocessRequested;
  }

  get holdOrPendingTitle(): string {
    return this.aiProcessing?.DocumentAiProcessingStatusId === DocumentAiProcessingStatusAllItems.Hold
      ? 'On Hold'
      : 'Pending';
  }

  get holdOrPendingMessage(): string {
    return this.aiProcessing?.DocumentAiProcessingStatusId === DocumentAiProcessingStatusAllItems.Hold
      ? 'AI processing is currently on hold for this document.'
      : 'AI processing has not been started for this document yet.';
  }

  get reprocessPendingMessage(): string {
    return 'Reprocess requested. Waiting for AI document processing to begin.';
  }

  get localDateTimeFormat(): string {
    return DocumentItemAiProcessingComponent.LOCAL_DATE_TIME_FORMAT;
  }

  get summaryStatusDateLabel(): string {
    return this.aiProcessing?.DocumentAiProcessingCompletedDateTime ? 'Completed' : 'Last Updated';
  }

  get processingDurationText(): string {
    const startedDateTime = this.aiProcessing?.DocumentAiProcessingStartDateTime;

    if (!startedDateTime) {
      return 'N/A';
    }

    const started = new Date(startedDateTime);
    const isComplete = this.isDocumentProcessingComplete();
    const endDateTime = isComplete
      ? this.aiProcessing?.DocumentAiProcessingLastUpdatedDateTime
      : this.aiProcessing?.DocumentAiProcessingCompletedDateTime;

    if (isComplete && !endDateTime) {
      return 'N/A';
    }

    const end = endDateTime ? new Date(endDateTime) : this.currentUtcNow;
    const totalSeconds = Math.max(0, Math.floor((end.getTime() - started.getTime()) / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    }

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }

    return `${seconds}s`;
  }

  getNormalizedPageNumbers(pageNumbers?: string): string {
    return this.buildPageNumberLabels(pageNumbers).join(', ');
  }

  isCourseExpanded(courseDocumentItemId?: number): boolean {
    return !!courseDocumentItemId && this.expandedCourseDocumentItemId === courseDocumentItemId;
  }

  isModuleExpanded(module: DocumentItemAiProcessingCourseModule): boolean {
    return this.expandedCourseModuleDocumentItemId === module.CourseModuleDocumentItemId;
  }

  isChunkExpanded(chunkId?: number): boolean {
    return !!chunkId && this.expandedChunkId === chunkId;
  }

  isPageExpanded(pageId?: number): boolean {
    return !!pageId && this.expandedPageId === pageId;
  }

  shouldShowPageToggle(pageText?: string): boolean {
    if (!pageText) {
      return false;
    }

    const normalizedText = pageText.trim();
    const lineCount = normalizedText.split(/\r?\n/).length;

    return normalizedText.length > 320 || lineCount > 4;
  }

  isChunkComplete(chunkStatusId?: number): boolean {
    return chunkStatusId === AIProcessingReferenceStatusAllItems.Success;
  }

  isChunkProcessing(chunkStatusId?: number): boolean {
    return chunkStatusId === AIProcessingReferenceStatusAllItems.Processing;
  }

  isChunkFilterActive(filter: 'instructional' | 'other' | 'all'): boolean {
    return this.chunkFilter === filter;
  }

  canReprocessChunk(chunk: { DocumentItemChunkId?: number; AIProcessingReferenceId?: number; AIProcessingStatusId?: number }): boolean {
    return !!chunk.DocumentItemChunkId
      && !!chunk.AIProcessingReferenceId
      && chunk.AIProcessingStatusId === AIProcessingReferenceStatusAllItems.Failed;
  }

  canForceReprocessChunk(chunk: { DocumentItemChunkId?: number; AIProcessingReferenceId?: number; AIProcessingStatusId?: number }): boolean {
    return !!chunk.DocumentItemChunkId
      && !!chunk.AIProcessingReferenceId
      && chunk.AIProcessingStatusId !== AIProcessingReferenceStatusAllItems.Processing
      && chunk.AIProcessingStatusId !== AIProcessingReferenceStatusAllItems.New
      && chunk.AIProcessingStatusId !== AIProcessingReferenceStatusAllItems.Reprocess;
  }

  isChunkActionLoading(documentItemChunkId?: number): boolean {
    return !!documentItemChunkId && !!this.chunkActionLoadingById[documentItemChunkId];
  }

  isLoadingCourseDetails(courseDocumentItemId?: number): boolean {
    return !!courseDocumentItemId && !!this.loadingCourseDocumentItemIds[courseDocumentItemId];
  }

  isLoadingModuleLessons(courseModuleId?: number): boolean {
    return !!courseModuleId && !!this.loadingCourseModuleIds[courseModuleId];
  }

  getExpandedCourseModules(courseDocumentItemId?: number): DocumentItemAiProcessingCourseModule[] {
    if (!courseDocumentItemId) {
      return [];
    }

    return this.courseDetailsById[courseDocumentItemId]?.Modules || [];
  }

  getModuleLessonLinks(courseModuleId?: number): DocumentItemAiProcessingLessonLink[] {
    if (!courseModuleId) {
      return [];
    }

    return this.moduleLessonLinksByCourseModuleId[courseModuleId] || [];
  }

  getInactiveModuleLessonLinks(courseModuleId?: number): DocumentItemAiProcessingLessonLink[] {
    return courseModuleId ? this.moduleInactiveLessonLinksByCourseModuleId[courseModuleId] || [] : [];
  }

  isShowingInactiveModuleLessonLinks(courseModuleId?: number): boolean {
    return !!courseModuleId && !!this.showInactiveLessonLinksByCourseModuleId[courseModuleId];
  }

  toggleInactiveModuleLessonLinks(courseModuleId?: number): void {
    if (courseModuleId) {
      this.showInactiveLessonLinksByCourseModuleId[courseModuleId] = !this.showInactiveLessonLinksByCourseModuleId[courseModuleId];
    }
  }

  isLessonLinkActionLoading(courseLessonDocumentLinkId?: number): boolean {
    return !!courseLessonDocumentLinkId && !!this.lessonLinkActionLoadingById[courseLessonDocumentLinkId];
  }

  canReprocessCourse(course: DocumentItemAiProcessingCourseSummary): boolean {
    return !!course.CourseDocumentItemId
      && course.CourseDocumentProcessingStatusId === CourseDocumentProcessingStatusAllItems.Failed;
  }

  canForceReprocessCourse(course: DocumentItemAiProcessingCourseSummary): boolean {
    return !!course.CourseDocumentItemId
      && course.CourseDocumentProcessingStatusId !== CourseDocumentProcessingStatusAllItems.Queued
      && course.CourseDocumentProcessingStatusId !== CourseDocumentProcessingStatusAllItems.ProcessingModules;
  }

  canReprocessModule(module: DocumentItemAiProcessingCourseModule): boolean {
    return !!module.CourseModuleDocumentItemId
      && !!module.AiProcessingReferenceId
      && module.AIProcessingStatusId === AIProcessingReferenceStatusAllItems.Failed;
  }

  canForceReprocessModule(module: DocumentItemAiProcessingCourseModule): boolean {
    return !!module.CourseModuleDocumentItemId
      && !!module.AiProcessingReferenceId
      && module.AIProcessingStatusId !== AIProcessingReferenceStatusAllItems.Processing
      && module.AIProcessingStatusId !== AIProcessingReferenceStatusAllItems.New
      && module.AIProcessingStatusId !== AIProcessingReferenceStatusAllItems.Reprocess;
  }

  isCourseActionLoading(courseDocumentItemId?: number): boolean {
    return !!courseDocumentItemId && !!this.courseActionLoadingById[courseDocumentItemId];
  }

  isModuleActionLoading(courseModuleDocumentItemId?: number): boolean {
    return !!courseModuleDocumentItemId && !!this.moduleActionLoadingById[courseModuleDocumentItemId];
  }

  isActionMenuOpen(actionMenuKey: string): boolean {
    return this.openActionMenuKey === actionMenuKey;
  }

  toggleActionMenu(actionMenuKey: string): void {
    if (this.openActionMenuKey === actionMenuKey) {
      this.closeActionMenus();
      return;
    }

    this.openActionMenuKey = actionMenuKey;
    this.openAiReferenceChooserModuleId = undefined;
  }

  closeActionMenus(): void {
    this.openActionMenuKey = undefined;
    this.openAiReferenceChooserModuleId = undefined;
  }

  getChunkActionMenuKey(documentItemChunkId?: number): string {
    return `chunk-${documentItemChunkId || 'unknown'}`;
  }

  getCourseActionMenuKey(courseDocumentItemId?: number): string {
    return `course-${courseDocumentItemId || 'unknown'}`;
  }

  getModuleActionMenuKey(courseModuleDocumentItemId?: number): string {
    return `module-${courseModuleDocumentItemId || 'unknown'}`;
  }

  getLessonActionMenuKey(lessonLinkId?: number, courseLessonId?: number): string {
    return `lesson-${lessonLinkId || courseLessonId || 'unknown'}`;
  }

  getModuleLessonCountText(module: DocumentItemAiProcessingCourseModule): string {
    const linkedLessonCount = module.LinkedLessonCount || 0;
    const totalLessonCount = module.TotalLessonCount || 0;

    return `${linkedLessonCount} / ${totalLessonCount}`;
  }

  hasModuleAiReferenceOptions(module: DocumentItemAiProcessingCourseModule): boolean {
    return !!module.AiProcessingReferenceId || !!module.CourseModuleId;
  }

  hasModuleAiReferenceChooser(module: DocumentItemAiProcessingCourseModule): boolean {
    return !!module.CourseModuleId && this.openAiReferenceChooserModuleId === module.CourseModuleId;
  }

  getModuleAiReferenceOptions(module: DocumentItemAiProcessingCourseModule): DocumentItemAiProcessingReferenceOption[] {
    if (!module.CourseModuleId) {
      return [];
    }

    return this.moduleAiReferenceOptionsByCourseModuleId[module.CourseModuleId] || [];
  }

  get showViewerRail(): boolean {
    return !!this.viewerDocumentItem;
  }

  closeViewer(): void {
    this.viewerDocumentItem = null;
    this.viewerInitialPageNumber = undefined;
    this.viewerPageNumbers = undefined;
  }

  toggleChunk(chunkId?: number): void {
    if (!chunkId) {
      return;
    }

    this.expandedChunkId = this.expandedChunkId === chunkId ? undefined : chunkId;
  }

  reprocessChunks(): void {
    if (!this.documentItemId || !this.canReprocessChunks || this.loading) {
      return;
    }

    this.applyPendingChunkReprocessState(undefined, true);

    this.documentsRepositoryService.requestDocumentItemChunkAiReprocessAll(this.documentItemId)
      .subscribe(() => {
        this.closeActionMenus();
        this.loadAiProcessing(false);
      }, () => {
        this.closeActionMenus();
        this.loadAiProcessing(false);
      });
  }

  reprocessChunk(chunk: { DocumentItemChunkId?: number; ChunkIndex?: number }, force = false): void {
    const documentItemChunkId = chunk.DocumentItemChunkId;

    if (!this.documentItemId || !documentItemChunkId || this.loading || this.isChunkActionLoading(documentItemChunkId)) {
      return;
    }

    this.chunkActionLoadingById[documentItemChunkId] = true;
    this.applyPendingChunkReprocessState(chunk);

    this.documentsRepositoryService.requestDocumentItemChunkAiReprocess(this.documentItemId, documentItemChunkId, force)
      .subscribe(() => {
        this.chunkActionLoadingById[documentItemChunkId] = false;
        this.closeActionMenus();
        this.loadAiProcessing(false);
      }, () => {
        this.chunkActionLoadingById[documentItemChunkId] = false;
        this.closeActionMenus();
        this.loadAiProcessing(false);
      });
  }

  togglePage(pageId?: number): void {
    if (!pageId) {
      return;
    }

    this.expandedPageId = this.expandedPageId === pageId ? undefined : pageId;
  }

  toggleCourse(course: DocumentItemAiProcessingCourseSummary): void {
    const courseDocumentItemId = course.CourseDocumentItemId;

    if (!courseDocumentItemId) {
      return;
    }

    if (this.expandedCourseDocumentItemId === courseDocumentItemId) {
      this.expandedCourseDocumentItemId = undefined;
      this.expandedCourseModuleDocumentItemId = undefined;
      return;
    }

    this.expandedCourseDocumentItemId = courseDocumentItemId;
    this.expandedCourseModuleDocumentItemId = undefined;

    if (this.courseDetailsById[courseDocumentItemId]) {
      return;
    }

    this.loadingCourseDocumentItemIds[courseDocumentItemId] = true;

    this.documentsRepositoryService.getCourseDocumentItemAiProcessing(courseDocumentItemId)
      .subscribe(result => {
        this.courseDetailsById[courseDocumentItemId] = result;
        this.loadingCourseDocumentItemIds[courseDocumentItemId] = false;
      }, () => {
        this.loadingCourseDocumentItemIds[courseDocumentItemId] = false;
      });
  }

  reprocessCourse(course: DocumentItemAiProcessingCourseSummary, force = false): void {
    const courseDocumentItemId = course.CourseDocumentItemId;

    if (!this.documentItemId || !courseDocumentItemId || this.loading || this.isCourseActionLoading(courseDocumentItemId)) {
      return;
    }

    this.courseActionLoadingById[courseDocumentItemId] = true;

    this.documentsRepositoryService.requestCourseDocumentItemAiReprocess(this.documentItemId, courseDocumentItemId, force)
      .subscribe(() => {
        this.courseActionLoadingById[courseDocumentItemId] = false;
        this.closeActionMenus();
        this.resetExpandedState();
        this.loadAiProcessing(false);
      }, () => {
        this.courseActionLoadingById[courseDocumentItemId] = false;
        this.closeActionMenus();
        this.resetExpandedState();
        this.loadAiProcessing(false);
      });
  }

  toggleModule(course: DocumentItemAiProcessingCourseSummary, module: DocumentItemAiProcessingCourseModule): void {
    const courseModuleId = module.CourseModuleId;
    const courseModuleDocumentItemId = module.CourseModuleDocumentItemId;

    if (!this.documentItemId || !courseModuleId || !courseModuleDocumentItemId) {
      return;
    }

    if (this.expandedCourseModuleDocumentItemId === courseModuleDocumentItemId) {
      this.expandedCourseModuleDocumentItemId = undefined;
      return;
    }

    this.expandedCourseModuleDocumentItemId = courseModuleDocumentItemId;

    if (this.moduleLessonLinksByCourseModuleId[courseModuleId]) {
      return;
    }

    this.loadingCourseModuleIds[courseModuleId] = true;

    this.documentsRepositoryService.getDocumentLessonsForCourseModule(this.documentItemId, courseModuleId)
      .subscribe(result => {
        this.moduleLessonLinksByCourseModuleId[courseModuleId] = result.filter(link => link.IsActive);
        this.moduleInactiveLessonLinksByCourseModuleId[courseModuleId] = result.filter(link => !link.IsActive);
        this.loadingCourseModuleIds[courseModuleId] = false;
      }, () => {
        this.loadingCourseModuleIds[courseModuleId] = false;
      });
  }

  restoreLessonLink(courseModuleId: number | undefined, lesson: DocumentItemAiProcessingLessonLink): void {
    const linkId = lesson.CourseLessonDocumentLinkId;
    if (!this.documentItemId || !courseModuleId || !linkId || this.isLessonLinkActionLoading(linkId)) { return; }

    const replaceActive = !!lesson.ActiveLink;
    this.dwModalService.showConfirmDialog({
      showConfirmDialog: true,
      title: replaceActive ? 'Replace active lesson link' : 'Restore lesson link',
      message: replaceActive
        ? `Replace the current active link for ${lesson.LessonName || 'this lesson'} with the inactive link on page(s) ${lesson.PageNumbers || lesson.FirstPageNumber}?`
        : `Restore the inactive link for ${lesson.LessonName || 'this lesson'} on page(s) ${lesson.PageNumbers || lesson.FirstPageNumber}?`
    }).subscribe(confirmed => {
      if (!confirmed) { return; }
      this.lessonLinkActionLoadingById[linkId] = true;
      this.documentsRepositoryService.restoreCourseLessonDocumentLink(this.documentItemId!, linkId, replaceActive)
        .subscribe(() => {
          this.lessonLinkActionLoadingById[linkId] = false;
          delete this.moduleLessonLinksByCourseModuleId[courseModuleId];
          delete this.moduleInactiveLessonLinksByCourseModuleId[courseModuleId];
          this.expandedCourseModuleDocumentItemId = undefined;
          this.closeActionMenus();
        }, () => {
          this.lessonLinkActionLoadingById[linkId] = false;
        });
    });
  }

  reprocessModule(module: DocumentItemAiProcessingCourseModule, force = false): void {
    const courseModuleDocumentItemId = module.CourseModuleDocumentItemId;

    if (!this.documentItemId || !courseModuleDocumentItemId || this.loading || this.isModuleActionLoading(courseModuleDocumentItemId)) {
      return;
    }

    this.moduleActionLoadingById[courseModuleDocumentItemId] = true;

    this.documentsRepositoryService.requestCourseModuleDocumentItemAiReprocess(this.documentItemId, courseModuleDocumentItemId, force)
      .subscribe(() => {
        this.moduleActionLoadingById[courseModuleDocumentItemId] = false;
        this.closeActionMenus();
        this.resetExpandedState();
        this.loadAiProcessing(false);
      }, () => {
        this.moduleActionLoadingById[courseModuleDocumentItemId] = false;
        this.closeActionMenus();
        this.resetExpandedState();
        this.loadAiProcessing(false);
      });
  }

  openAiProcessingReference(aiProcessingReferenceId?: number): void {
    if (!aiProcessingReferenceId) {
      return;
    }

    this.closeActionMenus();
    window.open(
      `/lms-admin/ai/advanced/ai-processing-references/edit/${aiProcessingReferenceId}`,
      DocumentItemAiProcessingComponent.AI_PROCESSING_REFERENCE_TAB_NAME
    );
  }

  openModuleAiProcessingReferences(module: DocumentItemAiProcessingCourseModule): void {
    if (!module.CourseModuleId) {
      if (module.AiProcessingReferenceId) {
        this.openAiProcessingReference(module.AiProcessingReferenceId);
      }

      return;
    }

    const existingOptions = this.moduleAiReferenceOptionsByCourseModuleId[module.CourseModuleId];
    if (existingOptions?.length) {
      this.handleModuleAiReferenceOptionsLoaded(module, existingOptions);
      return;
    }

    this.documentsRepositoryService.getAiProcessingReferenceOptionsForCourseModule(module)
      .subscribe(options => this.handleModuleAiReferenceOptionsLoaded(module, options));
  }

  openChunkInDocument(chunk: { StartPageNumber?: number; EndPageNumber?: number }): void {
    const pageNumbers = this.buildSequentialPageNumbers(chunk.StartPageNumber, chunk.EndPageNumber);
    this.openDocumentViewer(chunk.StartPageNumber, pageNumbers);
    this.closeActionMenus();
  }

  openLessonInDocument(lesson: DocumentItemAiProcessingLessonLink): void {
    const firstPageNumber = lesson.FirstPageNumber || this.getFirstPageNumber(lesson.PageNumbers);
    this.openDocumentViewer(firstPageNumber, lesson.PageNumbers);
    this.closeActionMenus();
  }

  trackChunk(_: number, chunk: { DocumentItemChunkId?: number; ChunkIndex?: number }): number | string {
    return chunk.DocumentItemChunkId || chunk.ChunkIndex || _;
  }

  trackCourse(_: number, course: { CourseDocumentItemId?: number; CourseId?: number }): number | string {
    return course.CourseDocumentItemId || course.CourseId || _;
  }

  trackModule(_: number, module: { CourseModuleDocumentItemId?: number; CourseModuleId?: number }): number | string {
    return module.CourseModuleDocumentItemId || module.CourseModuleId || _;
  }

  trackLesson(_: number, lesson: { CourseLessonDocumentLinkId?: number; CourseLessonId?: number }): number | string {
    return lesson.CourseLessonDocumentLinkId || lesson.CourseLessonId || _;
  }

  private loadAiProcessing(showLoader: boolean = true): void {
    if (!this.documentItemId) {
      return;
    }

    if (showLoader) {
      this.loading = true;
      this.resetExpandedState();
    }

    this.loadAttempted = true;

    this.documentsRepositoryService.getDocumentItemAiProcessingSummary(this.documentItemId)
      .subscribe(result => {
        this.aiProcessing = result;
        this.updateStaleModuleProcessingWarning();
        this.updateProcessingDurationTimer();
        this.activeTab = this.getDefaultActiveTab();
        this.refreshExpandedCourseDetails();
        this.loading = false;
      }, () => {
        this.aiProcessing = null;
        this.showStaleModuleProcessingWarning = false;
        this.stopProcessingDurationTimer();
        this.loading = false;
      });
  }

  private setupPubSubRefresh(): void {
    if (this.pubSubRefreshSetup || !this.canUseAiProcessingPubSub()) {
      return;
    }

    // Ensure the shared WebSocket pubsub service is instantiated for document admin views.
    if (!this.dwPubSubService) {
      return;
    }

    this.pubSubRefreshSetup = true;

    this.dwEventService.getEventData<any>(
      DwPubSubEventTopics.messageReceived(DocumentItemAiProcessingComponent.DOCUMENT_AI_PROCESSING_UPDATED_TOPIC))
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        const payload = this.tryParsePubSubPayload(event?.Payload);

        if (!payload?.DocumentItemId || payload.DocumentItemId !== this.documentItemId) {
          return;
        }

        this.silentRefreshRequests$.next();
      });

    this.silentRefreshRequests$
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(() => this.loadAiProcessing(false));
  }

  private applyPendingReprocessState(): void {
    if (!this.aiProcessing) {
      this.aiProcessing = {} as DocumentItemAiProcessingSummary;
    }

    this.aiProcessing = {
      ...this.aiProcessing,
      DocumentAiProcessingStatusId: DocumentAiProcessingStatusAllItems.ReprocessRequested,
      DocumentAiProcessingStatus: 'Reprocess Requested',
      DocumentAiProcessingProgressText: 'Pending reprocess',
      DocumentAiProcessingErrorText: null,
      DocumentAiProcessingCompletedDateTime: null,
      DocumentAiProcessingEstimatedCompletedDateTime: null,
      DocumentAiProcessingLastUpdatedDateTime: new Date().toISOString()
    };

    this.updateProcessingDurationTimer();
    this.activeTab = 'page-extraction';
  }

  private applyPendingChunkReprocessState(
    chunk?: { DocumentItemChunkId?: number; ChunkIndex?: number },
    reprocessAll = false
  ): void {
    const nowIso = new Date().toISOString();

    if (!this.aiProcessing) {
      this.aiProcessing = {
        Chunks: [],
        Pages: [],
        Courses: []
      } as DocumentItemAiProcessingSummary;
    }

    const targetChunkId = chunk?.DocumentItemChunkId;
    const updatedChunks = (this.aiProcessing.Chunks || []).map(existingChunk => {
      if (reprocessAll || (targetChunkId && existingChunk.DocumentItemChunkId === targetChunkId)) {
        return {
          ...existingChunk,
          AIProcessingStatusId: AIProcessingReferenceStatusAllItems.Reprocess,
          AIProcessingStatus: 'Reprocess'
        };
      }

      return existingChunk;
    });

    const queuedChunkCount = updatedChunks.filter(existingChunk =>
      existingChunk.AIProcessingStatusId === AIProcessingReferenceStatusAllItems.Reprocess
      || existingChunk.AIProcessingStatusId === AIProcessingReferenceStatusAllItems.New
      || existingChunk.AIProcessingStatusId === AIProcessingReferenceStatusAllItems.Processing).length;
    const processedChunkCount = updatedChunks.filter(existingChunk =>
      existingChunk.AIProcessingStatusId === AIProcessingReferenceStatusAllItems.Success).length;
    const failedChunkCount = updatedChunks.filter(existingChunk =>
      existingChunk.AIProcessingStatusId === AIProcessingReferenceStatusAllItems.Failed).length;

    this.aiProcessing = {
      ...this.aiProcessing,
      Chunks: updatedChunks,
      DocumentAiProcessingStatusId: DocumentAiProcessingStatusAllItems.ProcessingDocumentChunks,
      DocumentAiProcessingStatus: 'Processing Document Chunks',
      DocumentAiProcessingProgressText: chunk?.ChunkIndex != null
        ? `Queued chunk ${chunk.ChunkIndex + 1} for reprocessing`
        : 'Queued chunk reprocessing',
      DocumentAiProcessingErrorText: null,
      DocumentAiProcessingCompletedDateTime: undefined,
      DocumentAiProcessingLastUpdatedDateTime: nowIso,
      DocumentAiProcessingStartDateTime: nowIso,
      DocumentAiProcessingTotalChunkCount: updatedChunks.length,
      DocumentAiProcessingProcessedChunkCount: processedChunkCount,
      DocumentAiProcessingFailedChunkCount: failedChunkCount,
      DocumentAiProcessingTotalModuleLinkCount: 0,
      DocumentAiProcessingProcessedModuleLinkCount: 0,
      DocumentAiProcessingFailedModuleLinkCount: 0
    };

    if (queuedChunkCount > 0 || reprocessAll) {
      this.activeTab = 'chunk-processing';
    }

    this.updateProcessingDurationTimer();
  }

  private updateProcessingDurationTimer(): void {
    this.stopProcessingDurationTimer();
    this.currentUtcNow = new Date();
    this.processingDurationLabel = this.isDocumentProcessingComplete() ? 'Time Processed' : 'Time Processing';
    this.updateStaleModuleProcessingWarning();

    if (!this.aiProcessing?.DocumentAiProcessingStartDateTime
      || this.aiProcessing?.DocumentAiProcessingCompletedDateTime
      || this.isDocumentProcessingComplete()) {
      return;
    }

    this.processingDurationTimerId = window.setInterval(() => {
      this.currentUtcNow = new Date();
      this.updateStaleModuleProcessingWarning();
    }, 1000);
  }

  private updateStaleModuleProcessingWarning(): void {
    const lastUpdated = this.aiProcessing?.DocumentAiProcessingLastUpdatedDateTime;
    const courses = this.aiProcessing?.Courses || [];
    const allCoursesComplete = courses.length > 0 && courses.every(course =>
      course.CourseDocumentProcessingStatusId === CourseDocumentProcessingStatusAllItems.Complete);
    const lastUpdatedMilliseconds = lastUpdated ? new Date(lastUpdated).getTime() : NaN;

    this.showStaleModuleProcessingWarning = this.aiProcessing?.DocumentAiProcessingStatusId
      === DocumentAiProcessingStatusAllItems.ProcessingModuleDocumentLinks
      && allCoursesComplete
      && !Number.isNaN(lastUpdatedMilliseconds)
      && this.currentUtcNow.getTime() - lastUpdatedMilliseconds
        >= DocumentItemAiProcessingComponent.STALE_MODULE_PROCESSING_MILLISECONDS;
  }

  private stopProcessingDurationTimer(): void {
    if (!this.processingDurationTimerId) {
      return;
    }

    window.clearInterval(this.processingDurationTimerId);
    this.processingDurationTimerId = undefined;
  }

  private canUseAiProcessingPubSub(): boolean {
    return this.dwSecurityUserService.hasPermission(AppPermissions.contentAdmin)
      || this.dwSecurityUserService.hasPermission(AppPermissions.lMSDocumentsCanManageAll);
  }

  private tryParsePubSubPayload(payload: any): { DocumentItemId?: number } | null {
    if (!payload) {
      return null;
    }

    if (typeof payload === 'string') {
      try {
        return JSON.parse(payload);
      } catch {
        return null;
      }
    }

    return payload;
  }

  private isInstructionalChunk(documentItemChunkContentTypeId?: number): boolean {
    return !documentItemChunkContentTypeId
      || documentItemChunkContentTypeId === DocumentItemChunkContentTypeAllItems.Instructional;
  }

  private isDocumentProcessingComplete(): boolean {
    return this.aiProcessing?.DocumentAiProcessingStatusId === DocumentAiProcessingStatusAllItems.Complete;
  }

  private resetExpandedState(): void {
    this.closeActionMenus();
    this.expandedPageId = undefined;
    this.expandedChunkId = undefined;
    this.expandedCourseDocumentItemId = undefined;
    this.expandedCourseModuleDocumentItemId = undefined;
    this.courseDetailsById = {};
    this.moduleLessonLinksByCourseModuleId = {};
    this.moduleInactiveLessonLinksByCourseModuleId = {};
    this.showInactiveLessonLinksByCourseModuleId = {};
    this.lessonLinkActionLoadingById = {};
    this.moduleAiReferenceOptionsByCourseModuleId = {};
    this.loadingCourseDocumentItemIds = {};
    this.loadingCourseModuleIds = {};
    this.courseActionLoadingById = {};
    this.moduleActionLoadingById = {};
  }

  private handleModuleAiReferenceOptionsLoaded(
    module: DocumentItemAiProcessingCourseModule,
    options: DocumentItemAiProcessingReferenceOption[]
  ): void {
    if (!module.CourseModuleId) {
      return;
    }

    const normalizedOptions = (options || [])
      .slice()
      .sort((a, b) => a.SortOrder - b.SortOrder);

    this.moduleAiReferenceOptionsByCourseModuleId[module.CourseModuleId] = normalizedOptions;

    if (normalizedOptions.length <= 1) {
      this.openAiProcessingReference(normalizedOptions[0]?.AIProcessingReferenceId || module.AiProcessingReferenceId);
      return;
    }

    this.openAiReferenceChooserModuleId = module.CourseModuleId;
  }

  private openDocumentViewer(initialPageNumber?: number, pageNumbers?: string): void {
    if (!this.documentItemId) {
      return;
    }

    this.viewerInitialPageNumber = initialPageNumber;
    this.viewerPageNumbers = pageNumbers;

    if (this.viewerDocumentItem?.DocumentItemId === this.documentItemId) {
      return;
    }

    this.loadingViewerDocument = true;
    this.documentsRepositoryService.loadDocumentItem(this.documentItemId)
      .subscribe(documentItem => {
        this.viewerDocumentItem = documentItem;
        this.loadingViewerDocument = false;
      }, () => {
        this.loadingViewerDocument = false;
      });
  }

  private refreshExpandedCourseDetails(): void {
    const courseDocumentItemId = this.expandedCourseDocumentItemId;

    if (!courseDocumentItemId) {
      return;
    }

    this.documentsRepositoryService.getCourseDocumentItemAiProcessing(courseDocumentItemId)
      .subscribe(result => {
        this.courseDetailsById[courseDocumentItemId] = result;
      });
  }

  private getDefaultActiveTab(): DocumentAiProcessingTabKey {
    const statusId = this.aiProcessing?.DocumentAiProcessingStatusId;

    if (this.isExtractionFailed()) {
      return 'page-extraction';
    }

    if (this.isChunkFailed()) {
      return 'chunk-processing';
    }

    if (this.isChunkProcessingComplete()
      && (statusId === DocumentAiProcessingStatusAllItems.ProcessingModuleDocumentLinks
        || statusId === DocumentAiProcessingStatusAllItems.FailedProcessingModuleDocumentLinks
        || this.hasCourseProcessingStarted())) {
      return 'course-processing';
    }

    if (statusId === DocumentAiProcessingStatusAllItems.ProcessingDocumentChunks
      || statusId === DocumentAiProcessingStatusAllItems.FailedProcessingDocumentChunks
      || this.hasChunkProcessingStarted()) {
      return 'chunk-processing';
    }

    return 'page-extraction';
  }

  getPageExtractionPercent(): number {
    if (this.isPageExtractionComplete()) {
      return 100;
    }

    return 0;
  }

  getChunkProcessingPercent(): number {
    if (this.isChunkProcessingComplete()) {
      return 100;
    }

    return this.chunkPercent;
  }

  getCourseTabPercent(): number {
    if (this.isCourseProcessingComplete()) {
      return 100;
    }

    return this.coursePercent;
  }

  isPageExtractionComplete(): boolean {
    const statusId = this.aiProcessing?.DocumentAiProcessingStatusId;

    if (this.isExtractionFailed()) {
      return false;
    }

    return (this.aiProcessing?.PageCount || 0) > 0
      || statusId === DocumentAiProcessingStatusAllItems.ProcessingDocumentChunks
      || statusId === DocumentAiProcessingStatusAllItems.FailedProcessingDocumentChunks
      || statusId === DocumentAiProcessingStatusAllItems.ProcessingModuleDocumentLinks
      || statusId === DocumentAiProcessingStatusAllItems.FailedProcessingModuleDocumentLinks
      || statusId === DocumentAiProcessingStatusAllItems.Complete;
  }

  isChunkProcessingComplete(): boolean {
    const statusId = this.aiProcessing?.DocumentAiProcessingStatusId;
    const totalChunks = this.aiProcessing?.DocumentAiProcessingTotalChunkCount || 0;
    const processedChunks = this.aiProcessing?.DocumentAiProcessingProcessedChunkCount || 0;

    if (this.isChunkFailed()) {
      return false;
    }

    return statusId === DocumentAiProcessingStatusAllItems.ProcessingModuleDocumentLinks
      || statusId === DocumentAiProcessingStatusAllItems.FailedProcessingModuleDocumentLinks
      || statusId === DocumentAiProcessingStatusAllItems.Complete
      || (totalChunks > 0 && processedChunks >= totalChunks);
  }

  isCourseProcessingComplete(): boolean {
    const statusId = this.aiProcessing?.DocumentAiProcessingStatusId;
    const totalCourses = this.totalCourseCount;
    const completedCourses = this.completedCourseCount;

    if (this.isCourseFailed()) {
      return false;
    }

    return statusId === DocumentAiProcessingStatusAllItems.Complete
      || (totalCourses > 0 && completedCourses >= totalCourses);
  }

  private hasDocumentProcessingStarted(): boolean {
    return this.hasPageExtractionStarted() || this.hasChunkProcessingStarted();
  }

  private hasPageExtractionStarted(): boolean {
    const statusId = this.aiProcessing?.DocumentAiProcessingStatusId;

    if (statusId === DocumentAiProcessingStatusAllItems.New
      || statusId === DocumentAiProcessingStatusAllItems.ReprocessRequested) {
      return false;
    }

    return statusId === DocumentAiProcessingStatusAllItems.ExtractingPagesAndChunks
      || statusId === DocumentAiProcessingStatusAllItems.FailedExtractingPagesAndChunks
      || !!this.aiProcessing?.DocumentAiProcessingStartDateTime
      || (this.aiProcessing?.PageCount || 0) > 0;
  }

  private hasChunkProcessingStarted(): boolean {
    if (this.isExtractionFailed()) {
      return false;
    }

    const statusId = this.aiProcessing?.DocumentAiProcessingStatusId;

    return statusId === DocumentAiProcessingStatusAllItems.ProcessingDocumentChunks
      || statusId === DocumentAiProcessingStatusAllItems.FailedProcessingDocumentChunks
      || statusId === DocumentAiProcessingStatusAllItems.ProcessingModuleDocumentLinks
      || statusId === DocumentAiProcessingStatusAllItems.FailedProcessingModuleDocumentLinks
      || statusId === DocumentAiProcessingStatusAllItems.Complete
      || (this.aiProcessing?.DocumentAiProcessingTotalChunkCount || 0) > 0
      || this.chunkPercent > 0;
  }

  private hasCourseProcessingStarted(): boolean {
    if (this.isExtractionFailed() || this.isChunkFailed()) {
      return false;
    }

    const statusId = this.aiProcessing?.DocumentAiProcessingStatusId;

    return statusId === DocumentAiProcessingStatusAllItems.ProcessingModuleDocumentLinks
      || statusId === DocumentAiProcessingStatusAllItems.FailedProcessingModuleDocumentLinks
      || statusId === DocumentAiProcessingStatusAllItems.Complete
      || this.totalCourseCount > 0
      || this.coursePercent > 0
      || (this.aiProcessing?.Courses || []).some(course => !!course.CourseDocumentProcessingStatusId
        && course.CourseDocumentProcessingStatusId !== CourseDocumentProcessingStatusAllItems.New);
  }

  isPageExtractionFailed(): boolean {
    return this.isExtractionFailed();
  }

  isChunkProcessingFailed(): boolean {
    return this.isChunkFailed();
  }

  isCourseProcessingFailed(): boolean {
    return this.isCourseFailed();
  }

  private getCurrentCourseProcessingItem(): DocumentItemAiProcessingCourseSummary | undefined {
    const courses = this.aiProcessing?.Courses || [];

    return courses.find(course =>
      course.CourseDocumentProcessingStatusId === CourseDocumentProcessingStatusAllItems.ProcessingModules)
      || courses.find(course =>
        course.CourseDocumentProcessingStatusId === CourseDocumentProcessingStatusAllItems.Queued
        || course.CourseDocumentProcessingStatusId === CourseDocumentProcessingStatusAllItems.ReprocessRequested)
      || courses.find(course =>
        course.CourseDocumentProcessingStatusId === CourseDocumentProcessingStatusAllItems.New);
  }

  private getPercent(processed?: number, total?: number): number {
    if (!total || total <= 0) {
      return 0;
    }

    return Math.round(((processed || 0) / total) * 100);
  }

  private buildPageNumberLabels(pageNumbers?: string): string[] {
    if (!pageNumbers) {
      return [];
    }

    const uniqueSortedPages = Array.from(new Set(
      pageNumbers
        .split(',')
        .map(pageText => parseInt(pageText.trim(), 10))
        .filter(pageNumber => !isNaN(pageNumber) && pageNumber > 0)
    )).sort((a, b) => a - b);

    if (!uniqueSortedPages.length) {
      return [];
    }

    const labels: string[] = [];
    let rangeStart = uniqueSortedPages[0];
    let previousPage = uniqueSortedPages[0];

    for (let index = 1; index <= uniqueSortedPages.length; index++) {
      const currentPage = uniqueSortedPages[index];
      const rangeContinues = currentPage === previousPage + 1;

      if (rangeContinues) {
        previousPage = currentPage;
        continue;
      }

      labels.push(rangeStart === previousPage
        ? `Page ${rangeStart}`
        : `Pages ${rangeStart}-${previousPage}`);

      rangeStart = currentPage;
      previousPage = currentPage;
    }

    return labels;
  }

  private getFirstPageNumber(pageNumbers?: string): number | undefined {
    if (!pageNumbers) {
      return undefined;
    }

    const parsedPageNumbers = pageNumbers
      .split(',')
      .map(pageText => parseInt(pageText.trim(), 10))
      .filter(pageNumber => !isNaN(pageNumber) && pageNumber > 0)
      .sort((a, b) => a - b);

    return parsedPageNumbers.length ? parsedPageNumbers[0] : undefined;
  }

  private buildSequentialPageNumbers(startPageNumber?: number, endPageNumber?: number): string | undefined {
    if (!startPageNumber || startPageNumber <= 0) {
      return undefined;
    }

    const finalEndPageNumber = endPageNumber && endPageNumber >= startPageNumber
      ? endPageNumber
      : startPageNumber;

    const pageNumbers: number[] = [];

    for (let pageNumber = startPageNumber; pageNumber <= finalEndPageNumber; pageNumber++) {
      pageNumbers.push(pageNumber);
    }

    return pageNumbers.join(',');
  }

  private isExtractionFailed(): boolean {
    return this.aiProcessing?.DocumentAiProcessingStatusId === DocumentAiProcessingStatusAllItems.FailedExtractingPagesAndChunks;
  }

  private isChunkFailed(): boolean {
    return this.aiProcessing?.DocumentAiProcessingStatusId === DocumentAiProcessingStatusAllItems.FailedProcessingDocumentChunks;
  }

  private isCourseFailed(): boolean {
    return this.aiProcessing?.DocumentAiProcessingStatusId === DocumentAiProcessingStatusAllItems.FailedProcessingModuleDocumentLinks;
  }
}
